// Pregnancy mini app: dating math, the care plan template (US / California /
// freestanding birth center), the decision map, blood-draw bundling, and the
// alert engine. Everything here is pure so web, mobile, CLI, and API agree.
//
// The template is planning guidance compiled from ACOG/CDC/CDPH norms, not
// medical advice: every surface shows PREGNANCY_PLAN_DISCLAIMER.

export const PREGNANCY_PLAN_DISCLAIMER =
  'Planning guide based on common US (ACOG/CDC) and California guidance. Your midwife has the final say: confirm windows, tests, and birth center policies with SF Birth Center.';

// ---------- dates ----------

const DAY_MS = 86_400_000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

function dayNumber(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
}

function isoFromDayNumber(day: number) {
  const date = new Date(day * DAY_MS);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

export function addDaysIso(iso: string, days: number) {
  return isoFromDayNumber(dayNumber(iso) + days);
}

export function daysBetweenIso(from: string, to: string) {
  return dayNumber(to) - dayNumber(from);
}

export function todayIso(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// ---------- gestational age ----------

export type GestationalAge = { totalDays: number; weeks: number; days: number };

/** Gestational age in completed days from an estimated due date (EDD = 40w0d). */
export function gestationalAgeOn(edd: string, date: string): GestationalAge {
  const totalDays = 280 - daysBetweenIso(date, edd);
  return { totalDays, weeks: Math.floor(totalDays / 7), days: ((totalDays % 7) + 7) % 7 };
}

export function dateAtGestationalAge(edd: string, weeks: number, days = 0) {
  return addDaysIso(edd, weeks * 7 + days - 280);
}

export function gaDays(weeks: number, days = 0) {
  return weeks * 7 + days;
}

export function formatGestationalAge(ga: GestationalAge | number) {
  const total = typeof ga === 'number' ? ga : ga.totalDays;
  return `${Math.floor(total / 7)}w ${((total % 7) + 7) % 7}d`;
}

export function trimesterForDays(totalDays: number): 1 | 2 | 3 {
  if (totalDays < gaDays(14)) return 1;
  if (totalDays < gaDays(28)) return 2;
  return 3;
}

/** Calendar-month count in the common US "month N of 9" convention. */
export function pregnancyMonthForDays(totalDays: number) {
  const weeks = Math.floor(totalDays / 7);
  if (weeks <= 4) return 1;
  if (weeks <= 8) return 2;
  if (weeks <= 13) return 3;
  if (weeks <= 17) return 4;
  if (weeks <= 22) return 5;
  if (weeks <= 27) return 6;
  if (weeks <= 31) return 7;
  if (weeks <= 35) return 8;
  return 9;
}

export type PregnancyStage =
  | { kind: 'undated' }
  | { kind: 'before'; edd: string; daysUntilLmp: number }
  | { kind: 'pregnant'; edd: string; ga: GestationalAge; weekNumber: number; trimester: 1 | 2 | 3; month: number; daysToDue: number }
  | { kind: 'postpartum'; edd: string; birthDate: string; daysSinceBirth: number };

export function pregnancyStageOn(data: PregnancyData, date: string): PregnancyStage {
  const dating = resolvePregnancyDating(data.dating);
  if (!dating) return { kind: 'undated' };
  const { edd } = dating;
  const birthDate = data.outcome.birthDate;
  if (birthDate && date >= birthDate) return { kind: 'postpartum', edd, birthDate, daysSinceBirth: daysBetweenIso(birthDate, date) };
  const ga = gestationalAgeOn(edd, date);
  if (ga.totalDays < 0) return { kind: 'before', edd, daysUntilLmp: -ga.totalDays };
  return { kind: 'pregnant', edd, ga, weekNumber: ga.weeks + 1, trimester: trimesterForDays(ga.totalDays), month: pregnancyMonthForDays(ga.totalDays), daysToDue: daysBetweenIso(date, edd) };
}

export function describePregnancyStage(stage: PregnancyStage) {
  switch (stage.kind) {
    case 'undated': return 'Add a last period date or ultrasound to date the pregnancy';
    case 'before': return `${stage.daysUntilLmp} days before the dating start`;
    case 'postpartum': return stage.daysSinceBirth === 0 ? 'Birth day' : `${stage.daysSinceBirth} days postpartum`;
    case 'pregnant': return `${formatGestationalAge(stage.ga)}, week ${stage.weekNumber}, month ${stage.month}, trimester ${stage.trimester}`;
  }
}

// ---------- dating ----------

export type PregnancyDatingMethod = 'lmp' | 'ultrasound' | 'ivf' | 'manual';

export type PregnancyDating = {
  /** auto applies the ACOG ultrasound-vs-LMP redating rule. */
  method: 'auto' | PregnancyDatingMethod;
  lmp: string | null;
  cycleLengthDays: number;
  ultrasoundDate: string | null;
  ultrasoundGaWeeks: number | null;
  ultrasoundGaDays: number | null;
  ivfTransferDate: string | null;
  ivfEmbryoAgeDays: 3 | 5 | null;
  manualEdd: string | null;
};

export type ResolvedPregnancyDating = {
  edd: string;
  method: PregnancyDatingMethod;
  lmpEdd: string | null;
  ultrasoundEdd: string | null;
  ivfEdd: string | null;
  discrepancyDays: number | null;
  redateThresholdDays: number | null;
  note: string;
};

export function eddFromLmp(lmp: string, cycleLengthDays = 28) {
  return addDaysIso(lmp, 280 + (cycleLengthDays - 28));
}

export function eddFromUltrasound(scanDate: string, gaWeeks: number, gaDaysPart = 0) {
  return addDaysIso(scanDate, 280 - gaDays(gaWeeks, gaDaysPart));
}

export function eddFromIvf(transferDate: string, embryoAgeDays: 3 | 5) {
  return addDaysIso(transferDate, 266 - embryoAgeDays);
}

/** ACOG Committee Opinion 700: redate to ultrasound when the LMP disagrees by more than this. */
export function ultrasoundRedateThresholdDays(gaAtScanDays: number) {
  if (gaAtScanDays < gaDays(9)) return 5;
  if (gaAtScanDays < gaDays(16)) return 7;
  if (gaAtScanDays < gaDays(22)) return 10;
  if (gaAtScanDays < gaDays(28)) return 14;
  return 21;
}

export function resolvePregnancyDating(dating: PregnancyDating): ResolvedPregnancyDating | null {
  const lmpEdd = dating.lmp ? eddFromLmp(dating.lmp, dating.cycleLengthDays) : null;
  const ultrasoundEdd = dating.ultrasoundDate && dating.ultrasoundGaWeeks != null
    ? eddFromUltrasound(dating.ultrasoundDate, dating.ultrasoundGaWeeks, dating.ultrasoundGaDays ?? 0)
    : null;
  const ivfEdd = dating.ivfTransferDate && dating.ivfEmbryoAgeDays ? eddFromIvf(dating.ivfTransferDate, dating.ivfEmbryoAgeDays) : null;
  let discrepancyDays: number | null = null;
  let redateThresholdDays: number | null = null;
  if (lmpEdd && ultrasoundEdd && dating.ultrasoundGaWeeks != null) {
    discrepancyDays = Math.abs(daysBetweenIso(lmpEdd, ultrasoundEdd));
    redateThresholdDays = ultrasoundRedateThresholdDays(gaDays(dating.ultrasoundGaWeeks, dating.ultrasoundGaDays ?? 0));
  }
  const base = { lmpEdd, ultrasoundEdd, ivfEdd, discrepancyDays, redateThresholdDays };

  const pick = (method: PregnancyDatingMethod, note: string): ResolvedPregnancyDating | null => {
    const edd = method === 'lmp' ? lmpEdd : method === 'ultrasound' ? ultrasoundEdd : method === 'ivf' ? ivfEdd : dating.manualEdd;
    return edd ? { ...base, edd, method, note } : null;
  };

  if (dating.method !== 'auto') return pick(dating.method, `Using the ${DATING_METHOD_LABELS[dating.method].toLowerCase()} you chose.`);
  if (ivfEdd) return pick('ivf', 'IVF transfer date gives the most precise dating.');
  if (lmpEdd && ultrasoundEdd && discrepancyDays != null && redateThresholdDays != null) {
    return discrepancyDays > redateThresholdDays
      ? pick('ultrasound', `Ultrasound differs from the LMP by ${discrepancyDays} days (more than ${redateThresholdDays}), so the ultrasound date is used.`)
      : pick('lmp', `Ultrasound agrees with the LMP within ${redateThresholdDays} days, so the LMP date is kept.`);
  }
  if (ultrasoundEdd) return pick('ultrasound', 'Dated by ultrasound.');
  if (lmpEdd) return pick('lmp', 'Dated by last menstrual period. An early ultrasound may adjust it.');
  if (dating.manualEdd) return pick('manual', 'Using the due date you entered.');
  return null;
}

export const DATING_METHOD_LABELS: Record<PregnancyDatingMethod, string> = {
  lmp: 'Last period',
  ultrasound: 'Ultrasound',
  ivf: 'IVF transfer',
  manual: 'Due date entered',
};

// ---------- data ----------

export type PregnancyAppointmentKind = 'prenatal' | 'ultrasound' | 'lab' | 'class' | 'other';
export type PregnancyAppointmentStatus = 'scheduled' | 'done' | 'cancelled';

export type PregnancyAppointment = {
  id: string;
  date: string;
  time: string;
  kind: PregnancyAppointmentKind;
  title: string;
  provider: string;
  location: string;
  notes: string;
  checkpointIds: string[];
  status: PregnancyAppointmentStatus;
};

export type PregnancyCheckpointOverride = 'done' | 'declined' | 'na';
export type PregnancyCheckpointState = { override: PregnancyCheckpointOverride | null; date: string | null; note: string };
export type PregnancyDecisionState = { choice: string | null; note: string; decidedAt: string | null };
export type PregnancyMeasurement = { id: string; date: string; weight: number | null; systolic: number | null; diastolic: number | null; fundalCm: number | null; fetalHeartRate: number | null; note: string };
export type PregnancyResult = { id: string; date: string; test: string; result: string; note: string };
export type PregnancyQuestion = { id: string; text: string; answer: string; done: boolean; createdAt: string };

export type PregnancyData = {
  dating: PregnancyDating;
  profile: Record<string, string>;
  decisions: Record<string, PregnancyDecisionState>;
  checkpoints: Record<string, PregnancyCheckpointState>;
  appointments: PregnancyAppointment[];
  measurements: PregnancyMeasurement[];
  results: PregnancyResult[];
  questions: PregnancyQuestion[];
  outcome: { birthDate: string | null };
  settings: { weightUnit: 'lb' | 'kg' };
};

export const APPOINTMENT_KIND_LABELS: Record<PregnancyAppointmentKind, string> = {
  prenatal: 'Prenatal visit',
  ultrasound: 'Ultrasound',
  lab: 'Lab draw',
  class: 'Class',
  other: 'Other',
};

export function newPregnancyId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyPregnancyData(): PregnancyData {
  return normalizePregnancyData(null);
}

const MAX_LIST = 500;
const text = (value: unknown, max = 4000) => (typeof value === 'string' ? value.slice(0, max) : '');
const isoOrNull = (value: unknown) => (isIsoDate(value) ? value : null);
const numOrNull = (value: unknown, min: number, max: number) => {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
  return Number.isFinite(n) && n >= min && n <= max ? n : null;
};
const obj = (value: unknown): Record<string, unknown> => (value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {});
const list = (value: unknown) => (Array.isArray(value) ? value.slice(0, MAX_LIST).map(obj) : []);
const idOf = (value: unknown) => (typeof value === 'string' && value.trim() ? value.slice(0, 64) : newPregnancyId());
const oneOf = <T extends string>(value: unknown, options: readonly T[], fallback: T): T => (options.includes(value as T) ? value as T : fallback);

export function normalizePregnancyData(input: unknown): PregnancyData {
  const raw = obj(input);
  const dating = obj(raw.dating);
  const ultrasoundGaWeeks = numOrNull(dating.ultrasoundGaWeeks, 4, 42);
  const profileIn = obj(raw.profile);
  const profile: Record<string, string> = {};
  for (const field of PREGNANCY_PROFILE_FIELDS) {
    const value = text(profileIn[field.id], field.type === 'longtext' ? 4000 : 300).trim();
    if (value) profile[field.id] = value;
  }
  const decisions: Record<string, PregnancyDecisionState> = {};
  for (const [id, value] of Object.entries(obj(raw.decisions))) {
    const decision = PREGNANCY_DECISIONS.find((entry) => entry.id === id);
    if (!decision) continue;
    const state = obj(value);
    const choice = decision.options.some((option) => option.id === state.choice) ? state.choice as string : null;
    decisions[id] = { choice, note: text(state.note, 2000), decidedAt: typeof state.decidedAt === 'string' ? state.decidedAt.slice(0, 40) : null };
  }
  const checkpoints: Record<string, PregnancyCheckpointState> = {};
  for (const [id, value] of Object.entries(obj(raw.checkpoints))) {
    if (!PREGNANCY_CHECKPOINTS.some((entry) => entry.id === id)) continue;
    const state = obj(value);
    checkpoints[id] = {
      override: state.override === 'done' || state.override === 'declined' || state.override === 'na' ? state.override : null,
      date: isoOrNull(state.date),
      note: text(state.note, 2000),
    };
  }
  const knownCheckpoints = new Set(PREGNANCY_CHECKPOINTS.map((entry) => entry.id));
  return {
    dating: {
      method: oneOf(dating.method, ['auto', 'lmp', 'ultrasound', 'ivf', 'manual'] as const, 'auto'),
      lmp: isoOrNull(dating.lmp),
      cycleLengthDays: numOrNull(dating.cycleLengthDays, 20, 45) ?? 28,
      ultrasoundDate: isoOrNull(dating.ultrasoundDate),
      ultrasoundGaWeeks: ultrasoundGaWeeks == null ? null : Math.floor(ultrasoundGaWeeks),
      ultrasoundGaDays: ultrasoundGaWeeks == null ? null : Math.floor(numOrNull(dating.ultrasoundGaDays, 0, 6) ?? 0),
      ivfTransferDate: isoOrNull(dating.ivfTransferDate),
      ivfEmbryoAgeDays: dating.ivfEmbryoAgeDays === 3 || dating.ivfEmbryoAgeDays === 5 ? dating.ivfEmbryoAgeDays : null,
      manualEdd: isoOrNull(dating.manualEdd),
    },
    profile,
    decisions,
    checkpoints,
    appointments: list(raw.appointments).filter((entry) => isIsoDate(entry.date)).map((entry) => ({
      id: idOf(entry.id),
      date: entry.date as string,
      time: /^([01]\d|2[0-3]):[0-5]\d$/.test(String(entry.time ?? '')) ? String(entry.time) : '',
      kind: oneOf(entry.kind, ['prenatal', 'ultrasound', 'lab', 'class', 'other'] as const, 'prenatal'),
      title: text(entry.title, 200),
      provider: text(entry.provider, 200),
      location: text(entry.location, 300),
      notes: text(entry.notes, 4000),
      checkpointIds: Array.isArray(entry.checkpointIds) ? [...new Set(entry.checkpointIds.filter((id): id is string => typeof id === 'string' && knownCheckpoints.has(id)))] : [],
      status: oneOf(entry.status, ['scheduled', 'done', 'cancelled'] as const, 'scheduled'),
    })).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)),
    measurements: list(raw.measurements).filter((entry) => isIsoDate(entry.date)).map((entry) => ({
      id: idOf(entry.id),
      date: entry.date as string,
      weight: numOrNull(entry.weight, 20, 700),
      systolic: numOrNull(entry.systolic, 50, 260),
      diastolic: numOrNull(entry.diastolic, 30, 180),
      fundalCm: numOrNull(entry.fundalCm, 1, 60),
      fetalHeartRate: numOrNull(entry.fetalHeartRate, 40, 260),
      note: text(entry.note, 2000),
    })).sort((a, b) => a.date.localeCompare(b.date)),
    results: list(raw.results).filter((entry) => isIsoDate(entry.date) && text(entry.test).trim()).map((entry) => ({
      id: idOf(entry.id),
      date: entry.date as string,
      test: text(entry.test, 200).trim(),
      result: text(entry.result, 1000),
      note: text(entry.note, 2000),
    })).sort((a, b) => a.date.localeCompare(b.date)),
    questions: list(raw.questions).filter((entry) => text(entry.text).trim()).map((entry) => ({
      id: idOf(entry.id),
      text: text(entry.text, 1000).trim(),
      answer: text(entry.answer, 2000),
      done: entry.done === true,
      createdAt: typeof entry.createdAt === 'string' ? entry.createdAt.slice(0, 40) : new Date(0).toISOString(),
    })),
    outcome: { birthDate: isoOrNull(obj(raw.outcome).birthDate) },
    settings: { weightUnit: obj(raw.settings).weightUnit === 'kg' ? 'kg' : 'lb' },
  };
}

// ---------- profile: what the midwife asks every time ----------

export type PregnancyProfileField = {
  id: string;
  label: string;
  group: 'pregnancy' | 'health' | 'history' | 'care-team';
  type: 'text' | 'longtext' | 'date' | 'number' | 'select';
  options?: string[];
  placeholder?: string;
};

export const PREGNANCY_PROFILE_GROUPS: { id: PregnancyProfileField['group']; label: string }[] = [
  { id: 'pregnancy', label: 'This pregnancy' },
  { id: 'health', label: 'Health' },
  { id: 'history', label: 'History' },
  { id: 'care-team', label: 'Care team and contacts' },
];

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];

export const PREGNANCY_PROFILE_FIELDS: PregnancyProfileField[] = [
  { id: 'positiveTestDate', label: 'Positive test date', group: 'pregnancy', type: 'date' },
  { id: 'conception', label: 'Conception', group: 'pregnancy', type: 'select', options: ['Natural', 'IUI', 'IVF', 'Other'] },
  { id: 'babyCount', label: 'Number of babies', group: 'pregnancy', type: 'select', options: ['1', '2', '3+'] },
  { id: 'babySex', label: 'Baby sex (if known)', group: 'pregnancy', type: 'select', options: ['Not known', 'Girl', 'Boy'] },
  { id: 'birthDateMom', label: 'Her date of birth', group: 'health', type: 'date' },
  { id: 'heightIn', label: 'Height (inches)', group: 'health', type: 'number', placeholder: '65' },
  { id: 'prePregnancyWeight', label: 'Pre-pregnancy weight', group: 'health', type: 'number' },
  { id: 'bloodType', label: 'Her blood type', group: 'health', type: 'select', options: BLOOD_TYPES },
  { id: 'partnerBloodType', label: 'Partner blood type', group: 'health', type: 'select', options: BLOOD_TYPES },
  { id: 'rubellaImmune', label: 'Rubella immune', group: 'health', type: 'select', options: ['Yes', 'No', 'Unknown'] },
  { id: 'varicellaImmune', label: 'Chickenpox immune', group: 'health', type: 'select', options: ['Yes', 'No', 'Unknown'] },
  { id: 'allergies', label: 'Allergies', group: 'health', type: 'longtext', placeholder: 'Medications, foods, latex' },
  { id: 'medications', label: 'Medications', group: 'health', type: 'longtext' },
  { id: 'supplements', label: 'Supplements', group: 'health', type: 'longtext', placeholder: 'Prenatal vitamin, folate, iron, D3' },
  { id: 'conditions', label: 'Medical conditions', group: 'health', type: 'longtext' },
  { id: 'lastPap', label: 'Last Pap smear', group: 'health', type: 'date' },
  { id: 'gravida', label: 'Gravida (total pregnancies incl. this one)', group: 'history', type: 'number' },
  { id: 'para', label: 'Para (prior births 20w+)', group: 'history', type: 'number' },
  { id: 'priorPregnancies', label: 'Prior pregnancies and births', group: 'history', type: 'longtext', placeholder: 'Year, weeks, outcome, complications' },
  { id: 'surgeries', label: 'Surgeries', group: 'history', type: 'longtext' },
  { id: 'familyHistoryMom', label: 'Her family history', group: 'history', type: 'longtext', placeholder: 'Diabetes, blood pressure, genetic conditions, twins' },
  { id: 'familyHistoryPartner', label: 'Partner family history', group: 'history', type: 'longtext' },
  { id: 'ancestry', label: 'Ancestry (carrier screening)', group: 'history', type: 'text' },
  { id: 'midwife', label: 'Midwife', group: 'care-team', type: 'text' },
  { id: 'birthCenterPhone', label: 'Birth center phone', group: 'care-team', type: 'text', placeholder: 'Main and after-hours line' },
  { id: 'transferHospital', label: 'Transfer hospital', group: 'care-team', type: 'text' },
  { id: 'pediatrician', label: 'Pediatrician', group: 'care-team', type: 'text' },
  { id: 'insurance', label: 'Insurance', group: 'care-team', type: 'text', placeholder: 'Plan, member ID' },
  { id: 'pharmacy', label: 'Pharmacy', group: 'care-team', type: 'text' },
  { id: 'doula', label: 'Doula', group: 'care-team', type: 'text' },
  { id: 'emergencyContact', label: 'Emergency contact', group: 'care-team', type: 'text' },
];

export function isRhNegative(data: PregnancyData): boolean | null {
  const type = data.profile.bloodType;
  if (!type || type === 'Unknown') return null;
  return type.endsWith('-');
}

// ---------- decisions ----------

export type PregnancyDecisionCategory = 'testing' | 'vaccines' | 'birth' | 'newborn';

export type PregnancyDecisionOption = { id: string; label: string; detail?: string; fewerDraws?: boolean };

export type PregnancyDecision = {
  id: string;
  title: string;
  category: PregnancyDecisionCategory;
  decideByWeek: number;
  context: string;
  options: PregnancyDecisionOption[];
  /** Option the plan assumes while undecided. */
  planAssumes: string;
  onlyIf?: 'rh-negative';
};

export const PREGNANCY_DECISION_CATEGORIES: { id: PregnancyDecisionCategory; label: string }[] = [
  { id: 'testing', label: 'Testing' },
  { id: 'vaccines', label: 'Vaccines' },
  { id: 'birth', label: 'Birth' },
  { id: 'newborn', label: 'Newborn' },
];

export const PREGNANCY_DECISIONS: PregnancyDecision[] = [
  {
    id: 'cfdna', title: 'Cell-free DNA screening (NIPT)', category: 'testing', decideByWeek: 10, planAssumes: 'yes-cpns',
    context: 'A blood screen for Down syndrome, trisomy 18 and 13, and sex chromosome conditions, from 10w0d. Screening, not diagnosis. California offers it through the state Prenatal Screening Program. Drawn at 10w+ it can share the first-trimester lab draw.',
    options: [
      { id: 'yes-cpns', label: 'Yes, via California PNS', detail: 'Bundle with first labs at 10w+', fewerDraws: true },
      { id: 'yes-private', label: 'Yes, private lab', detail: 'Still bundle with first labs at 10w+', fewerDraws: true },
      { id: 'no', label: 'No screening' },
    ],
  },
  {
    id: 'fetal-sex', title: 'Learn the baby’s sex', category: 'testing', decideByWeek: 10, planAssumes: 'learn-cfdna',
    context: 'cfDNA can report sex chromosomes about 1–2 weeks after the draw. The anatomy scan usually shows it at 18–22w. Tell the midwife and lab up front if you want it kept secret.',
    options: [
      { id: 'learn-cfdna', label: 'Learn from cfDNA' },
      { id: 'learn-anatomy', label: 'Wait for the anatomy scan' },
      { id: 'surprise', label: 'Keep it a surprise' },
    ],
  },
  {
    id: 'carrier', title: 'Carrier screening', category: 'testing', decideByWeek: 10, planAssumes: 'mom-blood',
    context: 'Checks whether parents carry recessive conditions (cystic fibrosis, SMA, hemoglobin disorders, often an expanded panel; Fragile X is tested in the mother). Early results leave time for follow-up. Testing the partner first by saliva can avoid adding anything for her, but X-linked conditions still need her sample.',
    options: [
      { id: 'mom-blood', label: 'Her, added to the first draw', fewerDraws: true },
      { id: 'partner-first', label: 'Partner first (saliva)', detail: 'Her only if partner screens positive', fewerDraws: true },
      { id: 'already-done', label: 'Already done' },
      { id: 'no', label: 'Skip' },
    ],
  },
  {
    id: 'early-a1c', title: 'Early diabetes check (A1c) with first labs', category: 'testing', decideByWeek: 10, planAssumes: 'ask',
    context: 'Recommended early when there are risk factors (higher BMI plus another risk, prior gestational diabetes, family history). It runs on the same tube as the first labs, so it costs no extra stick.',
    options: [
      { id: 'add', label: 'Add to the first draw', fewerDraws: true },
      { id: 'no', label: 'Not needed' },
      { id: 'ask', label: 'Ask the midwife' },
    ],
  },
  {
    id: 'nt-scan', title: 'Nuchal translucency (NT) ultrasound', category: 'testing', decideByWeek: 11, planAssumes: 'no',
    context: 'Ultrasound at 11w0d–13w6d. Optional when doing cfDNA, but it gives an early anatomy look. No blood involved.',
    options: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
    ],
  },
  {
    id: 'aspirin', title: 'Low-dose aspirin for preeclampsia prevention', category: 'testing', decideByWeek: 12, planAssumes: 'ask',
    context: 'Recommended (81 mg nightly) when there are preeclampsia risk factors, ideally started between 12 and 16 weeks.',
    options: [
      { id: 'yes', label: 'Yes, start 12–16w' },
      { id: 'no', label: 'No' },
      { id: 'ask', label: 'Ask the midwife' },
    ],
  },
  {
    id: 'diagnostic', title: 'Diagnostic testing if a screen is high-risk', category: 'testing', decideByWeek: 13, planAssumes: 'decide-later',
    context: 'Only matters if a screen comes back high-risk. CVS is done at 10–13w, amniocentesis at 15–20w. Deciding your stance now makes a stressful week easier.',
    options: [
      { id: 'would-consider', label: 'Would consider it' },
      { id: 'no', label: 'Would not' },
      { id: 'decide-later', label: 'Decide if it comes up' },
    ],
  },
  {
    id: 'fetal-rhd', title: 'Fetal RhD test (Rh-negative only)', category: 'testing', decideByWeek: 16, planAssumes: 'ask', onlyIf: 'rh-negative',
    context: 'If she is Rh-negative, a cfDNA test can tell whether the baby is Rh-negative too. If so, the 28-week RhoGAM shot can often be skipped. Ask if it can be added to an existing draw.',
    options: [
      { id: 'test', label: 'Test the baby’s RhD' },
      { id: 'skip', label: 'Skip, just get RhoGAM' },
      { id: 'ask', label: 'Ask the midwife' },
    ],
  },
  {
    id: 'afp', title: 'AFP blood test for neural tube defects', category: 'testing', decideByWeek: 15, planAssumes: 'no-anatomy',
    context: 'California PNS offers a separate AFP draw at 15–20w to screen for open neural tube defects. A detailed anatomy ultrasound detects most of these too, so many people skip the extra draw.',
    options: [
      { id: 'no-anatomy', label: 'Skip, rely on anatomy scan', fewerDraws: true },
      { id: 'yes', label: 'Yes, extra draw at 15–20w' },
    ],
  },
  {
    id: 'gdm', title: 'Gestational diabetes screening method', category: 'testing', decideByWeek: 24, planAssumes: 'one-hour',
    context: 'Done at 24–28w. The 1-hour glucose drink is one stick, and only a high result leads to the 3-hour test (four sticks). The 2-hour test is three sticks up front. Some midwives accept 1–2 weeks of home fingerstick readings instead; ask SFBC.',
    options: [
      { id: 'one-hour', label: '1-hour drink (one draw)', fewerDraws: true },
      { id: 'two-hour', label: '2-hour test (three draws)' },
      { id: 'monitoring', label: 'Home fingerstick log', detail: 'No venous draw for glucose, if SFBC accepts it' },
    ],
  },
  {
    id: 'tdap', title: 'Tdap vaccine (whooping cough)', category: 'vaccines', decideByWeek: 27, planAssumes: 'yes',
    context: 'Recommended every pregnancy at 27–36w, ideally early in that window, so antibodies pass to the baby.',
    options: [{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }],
  },
  {
    id: 'flu', title: 'Flu shot', category: 'vaccines', decideByWeek: 20, planAssumes: 'yes',
    context: 'Recommended during flu season in any trimester.',
    options: [{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }, { id: 'done', label: 'Already had it' }],
  },
  {
    id: 'rsv', title: 'RSV protection for the baby', category: 'vaccines', decideByWeek: 32, planAssumes: 'infant',
    context: 'Either she gets the RSV vaccine at 32–36w (September–January), or the baby gets nirsevimab antibodies after birth. Usually one or the other, not both.',
    options: [
      { id: 'maternal', label: 'Her vaccine at 32–36w' },
      { id: 'infant', label: 'Baby’s antibody shot' },
      { id: 'none', label: 'Neither' },
    ],
  },
  {
    id: 'doula', title: 'Hire a doula', category: 'birth', decideByWeek: 24, planAssumes: 'undecided',
    context: 'Popular doulas book months ahead. Ask whether the doula has attended births at SFBC.',
    options: [{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }, { id: 'undecided', label: 'Still deciding' }],
  },
  {
    id: 'cord-blood', title: 'Cord blood', category: 'birth', decideByWeek: 32, planAssumes: 'none',
    context: 'Options are none, public donation, or a private bank. Private banks ship a kit you bring to the birth. Check what SFBC supports.',
    options: [{ id: 'none', label: 'No banking' }, { id: 'public', label: 'Public donation' }, { id: 'private', label: 'Private bank' }],
  },
  {
    id: 'pain', title: 'Pain relief plan', category: 'birth', decideByWeek: 34, planAssumes: 'unmedicated',
    context: 'Birth centers offer water, movement, and other non-epidural comfort measures. An epidural means transferring to the hospital.',
    options: [
      { id: 'unmedicated', label: 'Unmedicated at SFBC' },
      { id: 'open', label: 'Open to transfer for an epidural' },
      { id: 'undecided', label: 'Undecided' },
    ],
  },
  {
    id: 'water-birth', title: 'Water birth', category: 'birth', decideByWeek: 34, planAssumes: 'maybe',
    context: 'Laboring and/or birthing in the tub.',
    options: [{ id: 'yes', label: 'Yes' }, { id: 'labor-only', label: 'Labor only' }, { id: 'maybe', label: 'See how it goes' }, { id: 'no', label: 'No' }],
  },
  {
    id: 'cord-clamping', title: 'Cord clamping', category: 'birth', decideByWeek: 36, planAssumes: 'delayed',
    context: 'Delayed clamping (at least 30–60 seconds) is standard at most birth centers. Some families wait until the cord stops pulsing.',
    options: [{ id: 'delayed', label: 'Delayed' }, { id: 'until-white', label: 'Wait until it stops pulsing' }, { id: 'standard', label: 'Whatever is standard' }],
  },
  {
    id: 'placenta', title: 'Placenta', category: 'birth', decideByWeek: 36, planAssumes: 'dispose',
    context: 'Keep, encapsulate, or let the center dispose of it. Encapsulation services need to be arranged ahead.',
    options: [{ id: 'dispose', label: 'Center disposes' }, { id: 'keep', label: 'Keep it' }, { id: 'encapsulate', label: 'Encapsulate' }],
  },
  {
    id: 'postdates', title: 'Plan if past the due date', category: 'birth', decideByWeek: 38, planAssumes: 'monitor',
    context: 'Birth centers usually require labor to start by around 41w6d–42w. Past 41w, expect monitoring (non-stress test and fluid check). Induction happens at the hospital.',
    options: [
      { id: 'monitor', label: 'Monitor, natural methods, wait' },
      { id: 'induce-41', label: 'Hospital induction around 41w' },
      { id: 'discuss', label: 'Discuss at 38–39w' },
    ],
  },
  {
    id: 'feeding', title: 'Feeding plan', category: 'newborn', decideByWeek: 34, planAssumes: 'breast',
    context: 'Lets the team plan lactation support. A class or lactation consultant before birth helps.',
    options: [{ id: 'breast', label: 'Breastfeeding' }, { id: 'combo', label: 'Combination' }, { id: 'formula', label: 'Formula' }],
  },
  {
    id: 'vitamin-k', title: 'Newborn vitamin K', category: 'newborn', decideByWeek: 36, planAssumes: 'injection',
    context: 'Prevents a rare but serious bleeding disorder. The single injection is the standard. Oral doses need several follow-ups and are less protective.',
    options: [{ id: 'injection', label: 'Injection' }, { id: 'oral', label: 'Oral series' }, { id: 'decline', label: 'Decline' }],
  },
  {
    id: 'eye-ointment', title: 'Newborn eye ointment', category: 'newborn', decideByWeek: 36, planAssumes: 'yes',
    context: 'Erythromycin ointment prevents certain eye infections. It is routinely given and required by many states.',
    options: [{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'Decline' }],
  },
  {
    id: 'hep-b', title: 'Hepatitis B vaccine for baby', category: 'newborn', decideByWeek: 36, planAssumes: 'birth',
    context: 'Recommended within 24 hours of birth. Birth centers may give it or defer to the pediatrician.',
    options: [{ id: 'birth', label: 'At birth' }, { id: 'pediatrician', label: 'At first pediatrician visit' }, { id: 'later', label: 'Later on the schedule' }],
  },
  {
    id: 'circumcision', title: 'Circumcision (if a boy)', category: 'newborn', decideByWeek: 36, planAssumes: 'na',
    context: 'Not done at birth centers; arranged with a pediatrician or urologist if wanted.',
    options: [{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }, { id: 'na', label: 'Not applicable' }],
  },
];

export function decisionChoice(data: PregnancyData, id: string) {
  return data.decisions[id]?.choice ?? null;
}

function effectiveChoice(data: PregnancyData, id: string) {
  const decision = PREGNANCY_DECISIONS.find((entry) => entry.id === id);
  return decisionChoice(data, id) ?? decision?.planAssumes ?? null;
}

export function decisionApplies(data: PregnancyData, decision: PregnancyDecision) {
  if (decision.onlyIf === 'rh-negative') return isRhNegative(data) === true;
  return true;
}

// ---------- checkpoints ----------

export type PregnancyCheckpointCategory = 'visit' | 'lab' | 'ultrasound' | 'vaccine' | 'prep' | 'postpartum';
export type PregnancyStick = 'venous' | 'fingerstick' | null;

export type PregnancyCheckpoint = {
  id: string;
  title: string;
  category: PregnancyCheckpointCategory;
  /** Window as gestational age in days, or days after birth when anchor is birth. */
  start: number;
  end: number;
  anchor: 'ga' | 'birth';
  stick: PregnancyStick;
  /** Venous checkpoints in the same bundle can share one draw. */
  bundle?: string;
  tests?: string[];
  summary: string;
  tip?: string;
  /** Decision whose (assumed) choice makes this checkpoint apply. */
  decision?: { id: string; anyOf: string[] };
  onlyIf?: 'rh-negative' | 'rubella-non-immune';
};

const ga = gaDays;

export const PREGNANCY_CHECKPOINTS: PregnancyCheckpoint[] = [
  { id: 'intake', title: 'First midwife (intake) visit', category: 'visit', anchor: 'ga', start: ga(6), end: ga(10, 6), stick: null, summary: 'Full history, due date, plan of care. Bring the Info tab.', tip: 'Ask to book the first lab draw at 10w0d or later so cfDNA and carrier screening ride along.' },
  { id: 'dating-ultrasound', title: 'Dating / viability ultrasound', category: 'ultrasound', anchor: 'ga', start: ga(7), end: ga(13, 6), stick: null, summary: 'Confirms heartbeat and due date. Most accurate before 14 weeks.' },
  {
    id: 'first-labs', title: 'First-trimester lab panel', category: 'lab', anchor: 'ga', start: ga(6), end: ga(13, 6), stick: 'venous', bundle: 'draw-first',
    tests: ['Blood type, Rh, antibody screen', 'CBC (anemia)', 'Rubella and varicella immunity', 'Hepatitis B and C', 'HIV', 'Syphilis'],
    summary: 'The standard prenatal panel. Urine culture and STI checks are urine or swab, not blood.',
  },
  { id: 'cfdna', title: 'Cell-free DNA (NIPT)', category: 'lab', anchor: 'ga', start: ga(10), end: ga(22, 6), stick: 'venous', bundle: 'draw-first', tests: ['cfDNA screen'], summary: 'Results in about 1–2 weeks.', decision: { id: 'cfdna', anyOf: ['yes-cpns', 'yes-private'] } },
  { id: 'carrier-screen', title: 'Carrier screening', category: 'lab', anchor: 'ga', start: ga(4), end: ga(13, 6), stick: 'venous', bundle: 'draw-first', tests: ['Carrier panel'], summary: 'Earlier is better so partner testing fits in.', decision: { id: 'carrier', anyOf: ['mom-blood'] } },
  { id: 'carrier-partner', title: 'Partner carrier screening', category: 'lab', anchor: 'ga', start: ga(4), end: ga(12), stick: null, summary: 'Partner tests first, often by saliva kit. Her sample only if partner screens positive (plus any X-linked panel she chooses).', decision: { id: 'carrier', anyOf: ['partner-first'] } },
  { id: 'early-a1c', title: 'Early A1c', category: 'lab', anchor: 'ga', start: ga(6), end: ga(13, 6), stick: 'venous', bundle: 'draw-first', tests: ['Hemoglobin A1c'], summary: 'Early diabetes check, same tube as first labs.', decision: { id: 'early-a1c', anyOf: ['add'] } },
  { id: 'nt-scan', title: 'NT ultrasound', category: 'ultrasound', anchor: 'ga', start: ga(11), end: ga(13, 6), stick: null, summary: 'Hard window: 11w0d–13w6d. Book early.', decision: { id: 'nt-scan', anyOf: ['yes'] } },
  { id: 'aspirin-start', title: 'Start low-dose aspirin', category: 'visit', anchor: 'ga', start: ga(12), end: ga(16), stick: null, summary: '81 mg nightly until about 36 weeks, if recommended.', decision: { id: 'aspirin', anyOf: ['yes'] } },
  { id: 'fetal-rhd', title: 'Fetal RhD cfDNA test', category: 'lab', anchor: 'ga', start: ga(10), end: ga(22), stick: 'venous', bundle: 'draw-first', tests: ['Fetal RhD (cfDNA)'], summary: 'Tells whether RhoGAM is needed.', onlyIf: 'rh-negative', decision: { id: 'fetal-rhd', anyOf: ['test'] } },
  { id: 'afp', title: 'AFP screen (neural tube)', category: 'lab', anchor: 'ga', start: ga(15), end: ga(20), stick: 'venous', bundle: 'draw-afp', tests: ['Maternal serum AFP'], summary: 'California PNS second draw.', decision: { id: 'afp', anyOf: ['yes'] } },
  { id: 'anatomy-scan', title: 'Anatomy ultrasound', category: 'ultrasound', anchor: 'ga', start: ga(18), end: ga(22, 6), stick: null, summary: 'Detailed look at baby’s organs, placenta, and growth. Usually around 20w.' },
  {
    id: 'gdm-lab', title: 'Glucose screening (lab)', category: 'lab', anchor: 'ga', start: ga(24), end: ga(28, 6), stick: 'venous', bundle: 'draw-third', tests: ['Glucose challenge'],
    summary: 'Drink, then a draw. Bundle everything else from the 28-week labs into the same stick.', decision: { id: 'gdm', anyOf: ['one-hour', 'two-hour'] },
  },
  { id: 'gdm-monitoring', title: 'Glucose screening (home log)', category: 'lab', anchor: 'ga', start: ga(24), end: ga(28, 6), stick: 'fingerstick', summary: 'Fasting and after-meal fingerstick readings for the period SFBC asks. Bring the log.', decision: { id: 'gdm', anyOf: ['monitoring'] } },
  {
    id: 'third-labs', title: 'Third-trimester labs', category: 'lab', anchor: 'ga', start: ga(24), end: ga(32, 6), stick: 'venous', bundle: 'draw-third',
    tests: ['CBC (anemia)', 'Syphilis rescreen (CDPH)', 'HIV rescreen if recommended', 'Antibody screen if Rh-negative'],
    summary: 'Do these on the same stick as the glucose test.',
  },
  { id: 'rhogam', title: 'RhoGAM shot', category: 'vaccine', anchor: 'ga', start: ga(28), end: ga(29, 6), stick: null, summary: 'Injection, not a blood draw. Also needed after any bleeding or trauma.', onlyIf: 'rh-negative' },
  { id: 'tdap', title: 'Tdap vaccine', category: 'vaccine', anchor: 'ga', start: ga(27), end: ga(36, 6), stick: null, summary: 'Earlier in the window is better.', decision: { id: 'tdap', anyOf: ['yes'] } },
  { id: 'flu', title: 'Flu shot', category: 'vaccine', anchor: 'ga', start: ga(6), end: ga(39), stick: null, summary: 'Any trimester during flu season.', decision: { id: 'flu', anyOf: ['yes'] } },
  { id: 'rsv-maternal', title: 'RSV vaccine (her)', category: 'vaccine', anchor: 'ga', start: ga(32), end: ga(36, 6), stick: null, summary: 'Only given September–January.', decision: { id: 'rsv', anyOf: ['maternal'] } },
  { id: 'kick-counts', title: 'Start daily kick counts', category: 'prep', anchor: 'ga', start: ga(28), end: ga(28, 6), stick: null, summary: 'Same time daily: 10 movements, usually within 2 hours. Call if it changes.' },
  { id: 'doula-hire', title: 'Hire doula', category: 'prep', anchor: 'ga', start: ga(12), end: ga(28), stick: null, summary: 'Interview two or three. Good ones book early.', decision: { id: 'doula', anyOf: ['yes', 'undecided'] } },
  { id: 'childbirth-class', title: 'Childbirth class', category: 'prep', anchor: 'ga', start: ga(26), end: ga(34), stick: null, summary: 'Plus a breastfeeding / newborn care class.' },
  { id: 'pediatrician', title: 'Choose pediatrician', category: 'prep', anchor: 'ga', start: ga(24), end: ga(34), stick: null, summary: 'Baby needs a visit within a few days of birth. Confirm they take your insurance.' },
  { id: 'hospital-prereg', title: 'Pre-register at transfer hospital', category: 'prep', anchor: 'ga', start: ga(28), end: ga(34), stick: null, summary: 'So a transfer in labor is paperwork-free.' },
  { id: 'cord-blood-kit', title: 'Cord blood kit', category: 'prep', anchor: 'ga', start: ga(28), end: ga(34), stick: null, summary: 'Enroll and get the kit.', decision: { id: 'cord-blood', anyOf: ['public', 'private'] } },
  { id: 'birth-supplies', title: 'Birth bag and supplies', category: 'prep', anchor: 'ga', start: ga(33), end: ga(36), stick: null, summary: 'SFBC’s supply list, bag packed, car seat installed.' },
  { id: 'home-visit-36', title: '36-week home visit / birth plan review', category: 'visit', anchor: 'ga', start: ga(35), end: ga(37), stick: null, summary: 'Review decisions here with the midwife.' },
  { id: 'gbs', title: 'Group B strep swab', category: 'lab', anchor: 'ga', start: ga(36), end: ga(37, 6), stick: null, summary: 'Swab, not blood. Can usually be self-collected.' },
  { id: 'hgb-recheck', title: 'Hemoglobin recheck', category: 'lab', anchor: 'ga', start: ga(35), end: ga(37, 6), stick: 'fingerstick', summary: 'Birth centers check iron before term. Ask if a fingerstick (HemoCue) is enough instead of a draw.' },
  { id: 'postdates', title: 'Past-due monitoring', category: 'visit', anchor: 'ga', start: ga(40, 1), end: ga(41, 6), stick: null, summary: 'Non-stress test and fluid check after 41w, and a plan before the birth center limit.' },
  { id: 'pp-home-visit', title: 'Postpartum home visit', category: 'postpartum', anchor: 'birth', start: 1, end: 3, stick: null, summary: 'Midwife checks mom and baby. Newborn screen (heel prick) and pulse-ox at 24–48 hours.' },
  { id: 'pediatrician-first', title: 'First pediatrician visit', category: 'postpartum', anchor: 'birth', start: 2, end: 5, stick: null, summary: 'Weight and jaundice check.' },
  { id: 'hearing-screen', title: 'Newborn hearing screen', category: 'postpartum', anchor: 'birth', start: 1, end: 30, stick: null, summary: 'Not always done at the birth center. Make sure it happens by one month.' },
  { id: 'insurance-baby', title: 'Add baby to insurance', category: 'postpartum', anchor: 'birth', start: 0, end: 30, stick: null, summary: 'Most plans require it within 30 days.' },
  { id: 'pp-visit', title: 'Postpartum check-in', category: 'postpartum', anchor: 'birth', start: 14, end: 21, stick: null, summary: 'Mood, healing, feeding.' },
  { id: 'pp-comprehensive', title: 'Comprehensive postpartum visit', category: 'postpartum', anchor: 'birth', start: 28, end: 84, stick: null, summary: 'By 12 weeks.' },
  { id: 'mmr-pp', title: 'MMR vaccine after birth', category: 'postpartum', anchor: 'birth', start: 0, end: 42, stick: null, summary: 'Needed because she isn’t rubella immune.', onlyIf: 'rubella-non-immune' },
];

export const CHECKPOINT_CATEGORY_LABELS: Record<PregnancyCheckpointCategory, string> = {
  visit: 'Visit',
  lab: 'Lab',
  ultrasound: 'Ultrasound',
  vaccine: 'Shot',
  prep: 'Prep',
  postpartum: 'After birth',
};

export const DRAW_BUNDLE_LABELS: Record<string, string> = {
  'draw-first': 'First-trimester draw',
  'draw-afp': 'AFP draw',
  'draw-third': '24–28 week draw',
};

/** Birth centers generally require labor to start in this window; confirm SFBC’s exact policy. */
export const BIRTH_CENTER_TERM_WINDOW = { start: ga(37), end: ga(41, 6) };

export function checkpointApplies(data: PregnancyData, checkpoint: PregnancyCheckpoint) {
  if (checkpoint.onlyIf === 'rh-negative' && isRhNegative(data) !== true) return false;
  if (checkpoint.onlyIf === 'rubella-non-immune' && data.profile.rubellaImmune !== 'No') return false;
  if (checkpoint.decision) {
    const choice = effectiveChoice(data, checkpoint.decision.id);
    if (!choice || !checkpoint.decision.anyOf.includes(choice)) return false;
  }
  return true;
}

export type CheckpointWindow = { startDate: string; endDate: string };

export function checkpointWindow(data: PregnancyData, checkpoint: PregnancyCheckpoint, edd: string): CheckpointWindow {
  if (checkpoint.anchor === 'birth') {
    const base = data.outcome.birthDate ?? edd;
    return { startDate: addDaysIso(base, checkpoint.start), endDate: addDaysIso(base, checkpoint.end) };
  }
  return { startDate: addDaysIso(edd, checkpoint.start - 280), endDate: addDaysIso(edd, checkpoint.end - 280) };
}

export function formatCheckpointWindow(checkpoint: PregnancyCheckpoint) {
  if (checkpoint.anchor === 'birth') return checkpoint.start === checkpoint.end ? `day ${checkpoint.start} after birth` : `days ${checkpoint.start}–${checkpoint.end} after birth`;
  return `${formatGestationalAge(checkpoint.start)} – ${formatGestationalAge(checkpoint.end)}`;
}

export type PregnancyCheckpointStatus = 'done' | 'declined' | 'na' | 'scheduled' | 'due' | 'soon' | 'upcoming' | 'missed';

export type CheckpointView = {
  checkpoint: PregnancyCheckpoint;
  window: CheckpointWindow;
  status: PregnancyCheckpointStatus;
  appointment: PregnancyAppointment | null;
  /** Linked appointment falls outside the window. */
  outsideWindow: boolean;
  /** Applies only because the related decision is still undecided. */
  pendingDecision: string | null;
};

/** Book things this far ahead of their window. */
export const PREGNANCY_SOON_DAYS = 21;

export const CHECKPOINT_STATUS_LABELS: Record<PregnancyCheckpointStatus, string> = {
  done: 'Done',
  declined: 'Declined',
  na: 'Not needed',
  scheduled: 'Scheduled',
  due: 'Window open, not booked',
  soon: 'Book soon',
  upcoming: 'Later',
  missed: 'Window passed',
};

export function checkpointViews(data: PregnancyData, today: string): CheckpointView[] {
  const dating = resolvePregnancyDating(data.dating);
  if (!dating) return [];
  const views: CheckpointView[] = [];
  for (const checkpoint of PREGNANCY_CHECKPOINTS) {
    if (!checkpointApplies(data, checkpoint)) continue;
    const window = checkpointWindow(data, checkpoint, dating.edd);
    const state = data.checkpoints[checkpoint.id];
    const linked = data.appointments.filter((appt) => appt.status !== 'cancelled' && appt.checkpointIds.includes(checkpoint.id));
    const doneAppt = linked.find((appt) => appt.status === 'done' || appt.date < today) ?? null;
    const futureAppt = linked.find((appt) => appt.status === 'scheduled' && appt.date >= today) ?? null;
    const appointment = futureAppt ?? doneAppt;
    const pendingDecision = checkpoint.decision && !decisionChoice(data, checkpoint.decision.id) ? checkpoint.decision.id : null;
    let status: PregnancyCheckpointStatus;
    if (state?.override === 'done' || state?.override === 'declined' || state?.override === 'na') status = state.override;
    else if (doneAppt && !futureAppt) status = 'done';
    else if (futureAppt) status = 'scheduled';
    else if (checkpoint.anchor === 'birth' && !data.outcome.birthDate) status = 'upcoming';
    else if (today > window.endDate) status = 'missed';
    else if (today >= window.startDate) status = 'due';
    else if (daysBetweenIso(today, window.startDate) <= PREGNANCY_SOON_DAYS) status = 'soon';
    else status = 'upcoming';
    const outsideWindow = Boolean(appointment && appointment.status !== 'done' && (appointment.date < window.startDate || appointment.date > window.endDate));
    views.push({ checkpoint, window, status, appointment, outsideWindow, pendingDecision });
  }
  return views.sort((a, b) => a.window.startDate.localeCompare(b.window.startDate) || a.window.endDate.localeCompare(b.window.endDate));
}

// ---------- blood draws ----------

export type BloodDrawPlan = {
  id: string;
  label: string;
  checkpointIds: string[];
  tests: string[];
  window: CheckpointWindow | null;
  status: 'done' | 'scheduled' | 'open';
  scheduledDates: string[];
};

/** Group venous checkpoints into the fewest draws: one per bundle, intersecting windows. */
export function planBloodDraws(data: PregnancyData, today: string): BloodDrawPlan[] {
  const views = checkpointViews(data, today).filter((view) => view.checkpoint.stick === 'venous' && view.status !== 'declined' && view.status !== 'na');
  const groups = new Map<string, CheckpointView[]>();
  for (const view of views) {
    const key = view.checkpoint.bundle ?? view.checkpoint.id;
    groups.set(key, [...(groups.get(key) ?? []), view]);
  }
  return [...groups.entries()].map(([id, members]) => {
    const startDate = members.map((m) => m.window.startDate).sort().at(-1)!;
    const endDate = members.map((m) => m.window.endDate).sort()[0];
    const scheduledDates = [...new Set(members.flatMap((m) => (m.appointment ? [m.appointment.date] : [])))].sort();
    const allDone = members.every((m) => m.status === 'done');
    return {
      id,
      label: DRAW_BUNDLE_LABELS[id] ?? members[0].checkpoint.title,
      checkpointIds: members.map((m) => m.checkpoint.id),
      tests: members.flatMap((m) => m.checkpoint.tests ?? [m.checkpoint.title]),
      window: startDate <= endDate ? { startDate, endDate } : null,
      status: allDone ? 'done' : scheduledDates.length ? 'scheduled' : 'open',
      scheduledDates,
    } satisfies BloodDrawPlan;
  }).sort((a, b) => (a.window?.startDate ?? '').localeCompare(b.window?.startDate ?? ''));
}

// ---------- visit cadence ----------

/** Typical prenatal visit spacing: every 4 weeks to 28w, every 2 to 36w, then weekly. */
export function visitIntervalDays(totalGaDays: number) {
  if (totalGaDays < ga(28)) return 28;
  if (totalGaDays < ga(36)) return 14;
  return 7;
}

// ---------- alerts ----------

export type PregnancyAlertLevel = 'action' | 'soon' | 'info';
export type PregnancyAlert = { id: string; level: PregnancyAlertLevel; title: string; detail: string; checkpointId?: string; decisionId?: string };

export function pregnancyAlerts(data: PregnancyData, today: string): PregnancyAlert[] {
  const alerts: PregnancyAlert[] = [];
  const dating = resolvePregnancyDating(data.dating);
  if (!dating) {
    return [{ id: 'dating', level: 'action', title: 'Add her last period date', detail: 'Everything else (weeks, windows, reminders) is calculated from it.' }];
  }
  const stage = pregnancyStageOn(data, today);
  const todayGa = gestationalAgeOn(dating.edd, today).totalDays;
  const delivered = stage.kind === 'postpartum';

  const allViews = checkpointViews(data, today);
  // Bundles with a booked draw: unbooked members get an "add to that draw" alert instead of "book".
  const bookedBundles = new Set(allViews.filter((view) => view.checkpoint.bundle && view.status === 'scheduled').map((view) => view.checkpoint.bundle));
  for (const view of allViews) {
    const { checkpoint, window } = view;
    if (checkpoint.bundle && bookedBundles.has(checkpoint.bundle) && (view.status === 'due' || view.status === 'soon')) continue;
    const range = `${formatShortDate(window.startDate)}–${formatShortDate(window.endDate)}`;
    if (view.outsideWindow && view.appointment) {
      alerts.push({ id: `outside-${checkpoint.id}`, level: 'action', checkpointId: checkpoint.id, title: `${checkpoint.title} is booked outside its window`, detail: `Booked ${formatShortDate(view.appointment.date)}, window is ${range} (${formatCheckpointWindow(checkpoint)}).` });
    }
    if (delivered && checkpoint.anchor === 'ga') continue;
    const verb = checkpoint.category === 'prep' ? 'To do' : 'Book';
    // An open window is urgent only when it closes soon; long windows (flu shot, doula) can wait.
    const closingSoon = daysBetweenIso(today, window.endDate) <= PREGNANCY_SOON_DAYS;
    if (view.status === 'due') alerts.push({ id: `due-${checkpoint.id}`, level: closingSoon ? 'action' : 'soon', checkpointId: checkpoint.id, title: `${verb}: ${checkpoint.title}`, detail: `Window open until ${formatShortDate(window.endDate)} (${formatCheckpointWindow(checkpoint)}).` });
    else if (view.status === 'soon') alerts.push({ id: `soon-${checkpoint.id}`, level: 'soon', checkpointId: checkpoint.id, title: `${verb} soon: ${checkpoint.title}`, detail: `Window ${range} (${formatCheckpointWindow(checkpoint)}).` });
    else if (view.status === 'missed') alerts.push({ id: `missed-${checkpoint.id}`, level: 'action', checkpointId: checkpoint.id, title: `Not recorded: ${checkpoint.title}`, detail: `Window ended ${formatShortDate(window.endDate)}. Mark it done or declined, or ask the midwife whether it can still happen.` });
  }

  if (!delivered) {
    for (const decision of PREGNANCY_DECISIONS) {
      if (!decisionApplies(data, decision) || decisionChoice(data, decision.id)) continue;
      const deadline = ga(decision.decideByWeek);
      if (todayGa >= deadline) alerts.push({ id: `decide-${decision.id}`, level: 'action', decisionId: decision.id, title: `Decide: ${decision.title}`, detail: `Target was ${decision.decideByWeek}w.` });
      else if (todayGa >= deadline - ga(3)) alerts.push({ id: `decide-${decision.id}`, level: 'soon', decisionId: decision.id, title: `Decide by ${decision.decideByWeek}w: ${decision.title}`, detail: `${formatShortDate(dateAtGestationalAge(dating.edd, decision.decideByWeek))}.` });
    }

    for (const draw of planBloodDraws(data, today)) {
      if (draw.scheduledDates.length > 1) {
        alerts.push({ id: `split-${draw.id}`, level: 'action', title: `${draw.label} is split across ${draw.scheduledDates.length} sticks`, detail: `Booked ${draw.scheduledDates.map(formatShortDate).join(' and ')}. These tests can share one draw${draw.window ? ` between ${formatShortDate(draw.window.startDate)} and ${formatShortDate(draw.window.endDate)}` : ''}.` });
      }
      if (draw.status === 'scheduled' && draw.window) {
        const views = allViews.filter((view) => draw.checkpointIds.includes(view.checkpoint.id) && !view.appointment && view.status !== 'done');
        for (const view of views) {
          alerts.push({ id: `bundle-${view.checkpoint.id}`, level: 'action', checkpointId: view.checkpoint.id, title: `Add ${view.checkpoint.title} to the ${formatShortDate(draw.scheduledDates[0])} draw`, detail: 'Same stick, no extra needle. Tell the midwife or lab ahead of time.' });
        }
      }
    }

    const prenatal = data.appointments.filter((appt) => appt.kind === 'prenatal' && appt.status !== 'cancelled');
    const last = [...prenatal].reverse().find((appt) => appt.date <= today) ?? null;
    const next = prenatal.find((appt) => appt.date > today) ?? null;
    if (stage.kind === 'pregnant' && todayGa >= ga(6)) {
      const interval = visitIntervalDays(todayGa);
      const dueBy = last ? addDaysIso(last.date, interval) : today;
      const every = interval === 7 ? 'every week' : `every ${interval / 7} weeks`;
      if (!next) {
        alerts.push({ id: 'cadence', level: dueBy <= addDaysIso(today, 7) ? 'action' : 'soon', title: 'No next prenatal visit booked', detail: `At ${formatGestationalAge(todayGa)} visits are usually ${every}. Book one by ${formatShortDate(dueBy)}.` });
      } else if (daysBetweenIso(dueBy, next.date) > 4) {
        alerts.push({ id: 'cadence', level: 'soon', title: 'Gap before the next visit', detail: `Next visit is ${formatShortDate(next.date)}. Visits are usually ${every} now, so around ${formatShortDate(dueBy)}.` });
      }
    }

    if (stage.kind === 'pregnant' && todayGa >= ga(40) && !data.outcome.birthDate) {
      alerts.push({ id: 'birth-center-limit', level: 'action', title: 'Approaching the birth center limit', detail: `Birth centers usually require labor to start by ${formatGestationalAge(BIRTH_CENTER_TERM_WINDOW.end)} (${formatShortDate(dateAtGestationalAge(dating.edd, 41, 6))}). Confirm the plan with SFBC.` });
    }
  }

  if (isRhNegative(data) === null && todayGa >= ga(12)) {
    alerts.push({ id: 'blood-type', level: 'info', title: 'Record her blood type', detail: 'It comes back with the first labs and decides whether RhoGAM applies.' });
  }

  const order: Record<PregnancyAlertLevel, number> = { action: 0, soon: 1, info: 2 };
  return alerts.sort((a, b) => order[a.level] - order[b.level]);
}

// ---------- calendar / day info ----------

export type PregnancyDayInfo = {
  date: string;
  stage: PregnancyStage;
  isWeekStart: boolean;
  opening: PregnancyCheckpoint[];
  closing: PregnancyCheckpoint[];
  open: CheckpointView[];
  appointments: PregnancyAppointment[];
  decisionsDue: PregnancyDecision[];
  birthCenterWindow: boolean;
};

export function pregnancyDayInfo(data: PregnancyData, date: string, views: CheckpointView[] = checkpointViews(data, date)): PregnancyDayInfo {
  const stage = pregnancyStageOn(data, date);
  const dating = resolvePregnancyDating(data.dating);
  const total = dating ? gestationalAgeOn(dating.edd, date).totalDays : null;
  return {
    date,
    stage,
    isWeekStart: total != null && total >= 0 && total % 7 === 0,
    opening: views.filter((view) => view.window.startDate === date).map((view) => view.checkpoint),
    closing: views.filter((view) => view.window.endDate === date).map((view) => view.checkpoint),
    open: views.filter((view) => date >= view.window.startDate && date <= view.window.endDate),
    appointments: data.appointments.filter((appt) => appt.date === date && appt.status !== 'cancelled'),
    decisionsDue: dating ? PREGNANCY_DECISIONS.filter((decision) => decisionApplies(data, decision) && dateAtGestationalAge(dating.edd, decision.decideByWeek) === date) : [],
    birthCenterWindow: total != null && total >= BIRTH_CENTER_TERM_WINDOW.start && total <= BIRTH_CENTER_TERM_WINDOW.end,
  };
}

// ---------- weight gain (IOM 2009) ----------

export function weightGainTarget(data: PregnancyData) {
  const heightIn = Number(data.profile.heightIn);
  const weight = Number(data.profile.prePregnancyWeight);
  if (!(heightIn > 0) || !(weight > 0)) return null;
  const pounds = data.settings.weightUnit === 'kg' ? weight * 2.20462 : weight;
  const bmi = (703 * pounds) / (heightIn * heightIn);
  const [lowLb, highLb, label] = bmi < 18.5 ? [28, 40, 'underweight'] : bmi < 25 ? [25, 35, 'normal'] : bmi < 30 ? [15, 25, 'overweight'] : [11, 20, 'obese'];
  const factor = data.settings.weightUnit === 'kg' ? 1 / 2.20462 : 1;
  return { bmi: Math.round(bmi * 10) / 10, category: label as string, low: Math.round(lowLb * factor), high: Math.round(highLb * factor), unit: data.settings.weightUnit };
}

// ---------- summaries ----------

export function formatShortDate(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Plain-text brief to read from or hand to the midwife. */
export function pregnancyVisitBrief(data: PregnancyData, date: string) {
  const lines: string[] = [];
  const stage = pregnancyStageOn(data, date);
  const dating = resolvePregnancyDating(data.dating);
  lines.push(`Visit brief for ${formatShortDate(date)}`);
  if (dating) lines.push(`Due ${formatShortDate(dating.edd)} (${DATING_METHOD_LABELS[dating.method].toLowerCase()}). ${describePregnancyStage(stage)}.`);
  if (data.dating.lmp) lines.push(`LMP ${formatShortDate(data.dating.lmp)}, cycle ${data.dating.cycleLengthDays} days.`);
  const views = checkpointViews(data, date).filter((view) => view.checkpoint.category !== 'prep' && date >= view.window.startDate && date <= view.window.endDate && !['done', 'declined', 'na'].includes(view.status));
  if (views.length) lines.push('', 'Due around this visit:', ...views.map((view) => `- ${view.checkpoint.title} (${formatCheckpointWindow(view.checkpoint)})`));
  const decided = PREGNANCY_DECISIONS.filter((decision) => decisionApplies(data, decision) && decisionChoice(data, decision.id));
  if (decided.length) {
    lines.push('', 'Our decisions:');
    for (const decision of decided) {
      const state = data.decisions[decision.id];
      const option = decision.options.find((entry) => entry.id === state.choice);
      lines.push(`- ${decision.title}: ${option?.label ?? state.choice}${state.note ? ` (${state.note})` : ''}`);
    }
  }
  lines.push('', 'Blood draws: please bundle tests into as few sticks as possible.');
  for (const draw of planBloodDraws(data, date).filter((entry) => entry.status !== 'done')) {
    lines.push(`- ${draw.label}${draw.window ? ` ${formatShortDate(draw.window.startDate)}–${formatShortDate(draw.window.endDate)}` : ''}: ${draw.tests.join(', ')}`);
  }
  const open = data.questions.filter((question) => !question.done);
  if (open.length) lines.push('', 'Our questions:', ...open.map((question) => `- ${question.text}`));
  const latest = data.measurements.at(-1);
  if (latest) {
    const parts = [latest.weight != null ? `weight ${latest.weight} ${data.settings.weightUnit}` : null, latest.systolic && latest.diastolic ? `BP ${latest.systolic}/${latest.diastolic}` : null].filter(Boolean);
    if (parts.length) lines.push('', `Last measured ${formatShortDate(latest.date)}: ${parts.join(', ')}.`);
  }
  return lines.join('\n');
}
